/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import {
  Vue, Component, Watch, Prop,
} from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import type { ApplyChannelData, ApplyProductData, CalcPlanListData } from '@/api/apply-product/types';
import type { CouponRolePo } from '@/api/coupon/types';
import { session } from '@/shared/utils/storage';
import { SessionEnum } from '@/enums/session';
import { RouterV2Enum } from '@/router/router-names';
import FormField from '@/components/form-field';
import { repayPlanModelType } from '@/po/ProductResult';
import type { FullAmountCouponType } from '@/views_v2/apply-product/types';
import style from './index.module.scss';
import type { UserSceneConfig, PeriodOption } from '../../types';

const ApplyProductStore = namespace('applyProduct');

@Component
export default class SelectCoupon extends Vue {
  @ApplyProductStore.Getter showFullAmountCouponTag!: boolean;

  @ApplyProductStore.State amount!: string;

  @ApplyProductStore.State fullAmountCoupon!: FullAmountCouponType;

  @ApplyProductStore.State maxAmount!: string;

  @ApplyProductStore.State period!: PeriodOption | null;

  @ApplyProductStore.State coupon!: CouponRolePo;

  @ApplyProductStore.State channel!: ApplyChannelData;

  @ApplyProductStore.State couponList!: CouponRolePo[];

  @ApplyProductStore.State isCouponVisible!: boolean;

  @ApplyProductStore.State sceneConfig!: UserSceneConfig;

  @ApplyProductStore.State product!: ApplyProductData

  @ApplyProductStore.State multiChannelRepayment!: string

  @ApplyProductStore.Getter isMarketingAmountExperiment!: boolean;

  @ApplyProductStore.Getter isDiscountModuleExperiment!: boolean;

  @ApplyProductStore.State planListData!: CalcPlanListData;

  @ApplyProductStore.Getter isCouponLimitExperiment!: boolean;

  @ApplyProductStore.Getter showCouponModule!: boolean;

  @ApplyProductStore.Action calcRepayPlans!: (payload?: {
    repayType: string;
    currentAmount: string;
  }) => Promise<void>;

  @ApplyProductStore.Action getCouponDiscountRateExperimentRes!: () => Promise<void>;

  // 卡券变化后需要重新计算还款计划试算，保证用户在优惠券列表选择优惠券后回退至提现页，会重新计算还款计划
  @Watch('coupon', { deep: true, immediate: true })
  private onCouponChange() {
    // 优惠券变化后，获取优惠券折扣率实验结果
    this.getCouponDiscountRateExperimentRes();
    this.calcRepayPlans({
      repayType: repayPlanModelType[this.currentRepayType],
      currentAmount: this.amount,
    });
  }

  // 默认展示优惠券名称
  private get filterCouponList(): CouponRolePo[] {
    const couponFilterList = this.couponList.filter(
      (item) => item.isFirst === true,
    );
    return couponFilterList || [];
  }

  // 默认展示优惠券名称
  private get getCounponName() {
    return this.filterCouponList.length && this.filterCouponList[0].couponName;
  }

  private get currentRepaymentLength() {
    return this.currentProductRepayMode.length;
  }

  private get currentProductRepayMode(): string[] {
    return (this.product
      && this.product.productInfo
      && this.product.productInfo.repaymentModel)
      || [];
  }

  private get currentRepayType(): string | number {
    if (!this.multiChannelRepayment) {
      return this.currentRepaymentLength && this.currentProductRepayMode[0];
    }
    return this.multiChannelRepayment;
  }

  /** 未选择优惠券时，展示内容 */
  private get couponText() {
    return `${this.couponList.length}张可用${this.showFullAmountCouponTag ? `·全额借款免息${this.fullAmountCoupon?.amount}` : ''}`;
  }

  private renderCouponField() {
    const firstCouponVisible = this.filterCouponList.length > 0 && this.isCouponVisible;

    /** 优惠模块优化实验 */
    if (this.isDiscountModuleExperiment) {
      return (
        <div class={style.discountModuleCoupon} onClick={this.onSelectCoupon.bind(this)}>
          <div class={style.title}>优惠券</div>
          <div class={style.content}>
            {this.planListData ? (
              <div>
                {this.coupon && this.coupon.couponName ? (
                <div class={style.selectedCoupon}>
                  <span class={style.couponName}>{this.coupon.couponName}</span>
                  {this.planListData?.couponDiscountAmount ? (
                    <span>
                      ，
                      <span class={style.discountAmount}>
                        -¥{this.planListData?.couponDiscountAmount}
                      </span>
                    </span>
                  ) : null}
                </div>
                ) : (
                  <span class={style.unSelectedCoupon}>
                    {firstCouponVisible ? this.getCounponName : this.couponText}
                  </span>
                )}
              </div>
            ) : (
              <span class={style.loading}>加载中...</span>
            )}
            <span class={style.arrowIcon} />
          </div>
        </div>
      );
    }

    /** 营销潜力额度实验 */
    if (this.isMarketingAmountExperiment) {
      return (
        <FormField name="优惠" onClick={this.onSelectCoupon.bind(this)}>
          {this.coupon && this.coupon.couponName ? (
            <span class={[style.availCoupon, style.selectedCoupon]}>
              <span class={style.couponIcon} />
              {this.coupon.couponName}
            </span>
          ) : (
            <span class={[style.availCoupon, style.unSelectedCoupon]}>
              {firstCouponVisible ? this.getCounponName : this.couponText}
            </span>
          )}
        </FormField>
      );
    }

    return (
      <FormField name="优惠券" onClick={this.onSelectCoupon.bind(this)} need-divider={true}>
        {this.coupon && this.coupon.couponName ? (
          <span class={style.availCoupon}>{this.coupon.couponName}</span>
        ) : (
          <span class={style.availCoupon}>
            {firstCouponVisible ? this.getCounponName : this.couponText}
          </span>
        )}
      </FormField>
    );
  }

  private render() {
    if (this.showCouponModule) {
      /** 提现页面_优惠券栏曝光 TODO: 这里也会上报两次 */
      this.$ubtModuleExposeNew('finance_loan_apply_couponCategory', {
        productId: this.channel.productId,
        amount: this.amount,
        period: this.period?.period,
      });
      return (
        <div
          class={[style.selectCoupon]}
          key={(this.product && this.product.productInfo && this.product.productInfo.productId)
          || new Date().valueOf()}>
            {this.renderCouponField()}
        </div>
      );
    }
    return null;
  }

  private getExperimentType(): string {
    if (this.isDiscountModuleExperiment) {
      return 'discountModuleExperiment';
    }
    if (this.isMarketingAmountExperiment) {
      return 'marketingAmountExperiment';
    }
    return 'normal';
  }

  private onSelectCoupon() {
    this.$ubtPageButtonNew(
      '臻有钱_提现申请_选择优惠券',
      {
        productId: this.channel && this.channel.productId,
        type: this.getExperimentType(),
      },
    );

    session.set(SessionEnum.selectCoupon, {
      amount: this.amount,
      commodityId: this.channel && this.channel.productId,
      selCurrentPeriod: this.period && this.period.value,
      repayType: repayPlanModelType[this.currentRepayType] || '2',
      capitalCode: (this.channel && this.channel.capitalCode) || '',
      couponCancelDisable: this.sceneConfig.couponCancelDisable,
      repayOnSchedule: !!this.product?.productInfo?.repayOnSchedule || false,
      maxAmount: this.maxAmount,
      showCouponTips: this.isCouponLimitExperiment,
    });

    this.$router.push({
      path: RouterV2Enum.selectedCoupon,
    });
  }
}
